import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, takeUntil, catchError, of, finalize, debounceTime, filter } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import { ActionButton } from '@shared/components/add-form/add-form.component';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { SearchOrderEsParam } from '@app/api/order-service/model/searchOrderEsParam';
import { PageInfoOrderPageVo } from '@app/api/order-service/model/pageInfoOrderPageVo';
import { OrderPageVo } from '@app/api/order-service/model/orderPageVo';
import { Router } from '@angular/router';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import { InternalOrderConfig } from '../../order/internal/internal.config';
import { orderStatusListForTab } from '@shared/order-status/order-status.util';
import { OrderItemDialogComponent } from '../../order/internal/components/order-item-dialog/order-item-dialog.component';
import { PermissionService } from '@app/core/services/permission.service';
import { P_OMS } from '@app/core/constants/permissions';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { ChannelIntegrationService } from '@app/api/channel-service/api/channelIntegration.service';
import { ReadyToShipRequestDTO } from '@app/api/channel-service/model/readyToShipRequestDTO';
import { ReadyToShipResponseDTO } from '@app/api/channel-service/model/readyToShipResponseDTO';
import { OrderSseService, OrderEvent } from '@app/core/services/order-sse.service';
import {
  buildLazadaMergeAwbUrl,
  parseOuterOrderNosToNumericIds,
} from '@app/feature/center-business/channel/utils/lazada-integration.util';
import { isShopeeChannelCode } from '@app/feature/center-business/channel/utils/shopee-integration.util';

@Component({
  selector: 'app-marketplace-order',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule,
    TagModule,
    TooltipModule,
    ToastModule,
    AppButtonComponent,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    OrderItemDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './marketplace-order.component.html',
  styleUrl: './marketplace-order.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketplaceOrderComponent implements OnInit, OnDestroy {
  /** SendStatus.PACKED from backend - only these orders can be "Ready to ship". */
  private static readonly SEND_STATUS_PACKED = '40510';
  /** Delivery status "Pending Ready-to-Ship" (code 10511). */
  private static readonly SEND_STATUS_READY_TO_SHIP_PENDING = '10511';
  /** Delivery status Ready to ship (code 10512) – only these rows show the Print waybill action. */
  private static readonly SEND_STATUS_READY_TO_SHIP = '10512';
  /** Shopee delivery states where the seller still needs to call ship_order (arrange shipment). */
  private static readonly SHOPEE_SHIPPABLE_STATUSES = [
    MarketplaceOrderComponent.SEND_STATUS_PACKED,
    MarketplaceOrderComponent.SEND_STATUS_READY_TO_SHIP_PENDING,
    MarketplaceOrderComponent.SEND_STATUS_READY_TO_SHIP,
  ];
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private orderService = inject(OrderControllerService);
  private http = inject(HttpClient);
  private translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private messageService = inject(MessageService);
  private channelIntegrationService = inject(ChannelIntegrationService);
  private readonly orderSseService = inject(OrderSseService);
  private refreshTrigger$ = new Subject<void>();

  /** Selected rows (for checkbox). Used by Ready to ship batch action. */
  selectedOrders = signal<OrderPageVo[]>([]);

  // Signals for reactive state management
  loading = signal(false);
  error = signal<string | null>(null);
  orders = signal<OrderPageVo[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(InternalOrderConfig.PAGINATION.defaultPageSize);

  // Local state for pagination
  rows = InternalOrderConfig.PAGINATION.defaultPageSize;
  first = 0;
  goToPageNumber = 1;

  // Permission flags - computed from PermissionService
  // Permission flags - using third-party.third-party_products permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('third-party.third-party_sales_orders.view');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('third-party.third-party_sales_orders.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('third-party.third-party_sales_orders.delete');
  });

  // Configuration from config file
  statusOptions = InternalOrderConfig.STATUS_FILTER_OPTIONS;
  orderSources = InternalOrderConfig.FORM_OPTIONS.orderSources;
  salesChannels = InternalOrderConfig.FORM_OPTIONS.salesChannels;
  buyerNotesOptions = InternalOrderConfig.FORM_OPTIONS.buyerNotesOptions;
  giftOptions = InternalOrderConfig.FORM_OPTIONS.giftOptions;

  // Custom search format without salesChannels filter
  searchFormat: FormFieldConfig[] = [
    {
      key: 'orderNumber',
      label: 'search.form.fields.order_number',
      type: 'text',
      placeholder: 'search.form.placeholders.order_number',
    },
    {
      key: 'productName',
      label: 'search.form.fields.product_name_code',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'skuCode',
      label: 'search.form.fields.sku_code',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'recipientPhone',
      label: 'search.form.fields.recipient_phone',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'buyerNickname',
      label: 'search.form.fields.buyer_nickname',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
  ];

  // Check if user has any action permissions (READ, UPDATE, DELETE)
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete()
  );

  // Table configuration - computed to conditionally include actions column
  columns = computed((): TableColumn[] => {
    const baseColumns = InternalOrderConfig.TABLE_COLUMNS.filter(
      (col) => col.prop !== 'actions'
    );
    if (this.hasAnyActionPermission()) {
      // Get the actions column from the original config to preserve the correct label
      const actionsColumn = InternalOrderConfig.TABLE_COLUMNS.find(
        (col) => col.prop === 'actions'
      );
      if (actionsColumn) {
        return [...baseColumns, actionsColumn];
      }
      // Fallback if actions column not found in config
      return [
        ...baseColumns,
        {
          label: 'order.internal.action',
          prop: 'actions',
          width: 130,
          type: 'actions' as const,
        },
      ];
    }
    return baseColumns;
  });

  // Advanced table columns split
  defaultColumns = computed((): TableColumn[] => {
    const defaultsOrder: string[] = [
      'innerOrderNo',
      'storeName',
      'goodsQuatity',
      'orderStatus',
      'fulfillmentStatus',
      'payStatus',
    ];
    const fromConfig = (prop: string) =>
      InternalOrderConfig.TABLE_COLUMNS.find((c) => c.prop === prop) as
        | TableColumn
        | undefined;

    const cols: TableColumn[] = [];
    for (const key of defaultsOrder) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig('actions');
      if (actionsCol) cols.push(actionsCol);
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const defaultProps = new Set(this.defaultColumns().map((c) => c.prop));
    return InternalOrderConfig.TABLE_COLUMNS.filter((c) => {
      // Exclude any default column
      if (defaultProps.has(c.prop)) return false;
      // Exclude actions if not permitted
      if (c.prop === 'actions' && !this.hasAnyActionPermission()) return false;
      return true;
    });
  });

  // Primary button configuration - always hidden for marketplace orders
  primaryButton = computed(() => ({
    label: 'order.internal.create_order',
    severity: 'info' as const,
    outlined: false,
    disabled: false,
    show: false, // Always hide create order button for marketplace orders
    icon: 'add',
  }));

  // Secondary button configuration for column config
  secondaryButton = computed(() => ({
    label: '',
    severity: 'success' as const,
    outlined: true,
    show: true,
    icon: 'view_column',
  }));

  // Computed values
  totalPages = computed(() => Math.ceil(this.totalRecords() / this.pageSize()));
  hasNextPage = computed(() => this.currentPage() < this.totalPages());
  hasPreviousPage = computed(() => this.currentPage() > 1);

  // Get orders data
  ordersData = computed(() => this.orders());

  // Order item dialog state
  showOrderItemDialog = signal(false);
  selectedOrderId = signal<number | null>(null);
  selectedOrderNumber = signal<string | null>(null);

  constructor() {
    // Component initialized
  }

  ngOnInit(): void {
    this.loadOrders();
    this.setupSseSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.orderSseService.disconnect();
  }

   /**
   * Setup SSE subscription for real-time order updates
   */
  private setupSseSubscription(): void {
    // Connect to SSE
    this.orderSseService.connect();

    // Debounce refresh to avoid multiple rapid refreshes
    this.refreshTrigger$
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadOrders();
      });

    // Subscribe to order events
    this.orderSseService.orderEvents
      .pipe(
        filter((event: OrderEvent) => event.eventType !== 'o2o-status-updated'),
        takeUntil(this.destroy$)
      )
      .subscribe((event: OrderEvent) => {
        // Trigger refresh for any order event
        this.refreshTrigger$.next();
      });
  }

  private loadOrders(searchData?: SearchFormData): void {
    this.loading.set(true);
    this.error.set(null);

    const searchParams: SearchOrderEsParam = {
      page: this.currentPage() - 1,
      size: this.pageSize(),
      sortBy: 'creationDate',
      ascending: false,
      businessType: 'MARKETPLACE',
      // Filter by the canonical business order status set for the active tab ('all' → no filter).
      orderStatusList: orderStatusListForTab(this.getActiveStatus()),
      ...(searchData && {
        ...(searchData['orderNumber'] && {
          orderNo: searchData['orderNumber'],
        }),
        ...(searchData['productName'] && {
          spuName: searchData['productName'],
        }),
        ...(searchData['skuCode'] && { skuCode: searchData['skuCode'] }),
        ...(searchData['recipientPhone'] && {
          mobile: searchData['recipientPhone'],
        }),
        ...(searchData['buyerNickname'] && {
          buyerNick: searchData['buyerNickname'],
        }),
      }),
    };

    this.orderService
      .orderPage1(searchParams, 'response', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: any) => {
          this.error.set(
            this.translateService.instant(
              'order.internal.messages.failed_to_load_orders'
            )
          );
          console.error('Error loading orders:', err);
          return of(null);
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe((response: any) => {
        if (response?.body) {
          this.orders.set(response.body.pageContent || []);
          this.totalRecords.set(response.body.totalElements || 0);
          this.currentPage.set((response.body.number ?? 0) + 1);
        }
      });
  }

  // Formatter functions
  formatDateTime = formatDateTime;
  formatCurrency = formatCurrencyVND;

  // Status formatter functions using config
  getOrderStatusLabel = InternalOrderConfig.getOrderStatusLabel;
  getPaymentStatusLabel = InternalOrderConfig.getPaymentStatusLabel;
  getDeliveryStatusLabel = InternalOrderConfig.getDeliveryStatusLabel;
  getReceivingStatusLabel = InternalOrderConfig.getReceivingStatusLabel;

  // Use shared status methods from InternalOrderConfig
  getStatusDisplayText = InternalOrderConfig.getStatusDisplayText;
  getStatusSeverity = InternalOrderConfig.getStatusSeverity;
  getStatusStyle = InternalOrderConfig.getStatusStyle;
  getStatusTooltip = InternalOrderConfig.getStatusTooltip;

  onSearch(searchData: SearchFormData): void {
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders(searchData);
  }

  onReset(): void {
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    const newPage = Math.floor((event.first ?? 0) / (event.rows ?? 20)) + 1;
    this.currentPage.set(newPage);
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.pageSize.set(event.rows ?? 20);
    this.loadOrders();
  }

  onGoToPage(): void {
    if (this.goToPageNumber < 1 || this.goToPageNumber > this.totalPages())
      return;

    this.currentPage.set(this.goToPageNumber);
    this.first = (this.goToPageNumber - 1) * this.pageSize();
    this.loadOrders();
  }

  /**
   * Get the active status from status options
   */
  private getActiveStatus():
    | 'all'
    | 'unfinished'
    | 'completed'
    | 'closed'
    | 'abnormal' {
    const activeStatus = this.statusOptions.find((status) => status.active);
    return (activeStatus ? activeStatus.key : 'all') as
      | 'all'
      | 'unfinished'
      | 'completed'
      | 'closed'
      | 'abnormal';
  }

  /**
   * Map order status to backend number
   * Backend constants:
   * - NO_COMPLETE = "0"
   * - COMPLETE = "1"
   * - CLOSE = "2"
   * - EXCEPTION = "3"
   * If status is 'all', return undefined to exclude from payload
   */
  onStatusChange(status: string): void {
    // Update status options
    this.statusOptions = this.statusOptions.map((option) => ({
      ...option,
      active: option.key === status,
    }));

    // Reset to first page when changing status
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders();
  }

  onSelectionChange(selection: any[]): void {
    this.selectedOrders.set(selection || []);
  }

  /** Read delivery/send status from row (camelCase or snake_case, number, string, or object with code/value). */
  private getDeliveryStatus(row: any): string {
    if (!row) return '';
    let raw =
      row.sendStatus ??
      row.fulfillmentStatus ??
      row.deliveryStatus ??
      row.send_status ??
      row.delivery_status ??
      row.syncDeliveryStatus ??
      row.sync_delivery_status ??
      '';
    if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
      raw = (raw as { code?: unknown; value?: unknown; id?: unknown }).code
        ?? (raw as { value?: unknown }).value
        ?? (raw as { id?: unknown }).id
        ?? '';
    }
    return String(raw).trim();
  }

  /** Resolve selected item to current row from orders() by id so we read the same delivery status as displayed. */
  private resolveToCurrentOrder(selectedItem: any): any {
    const id = selectedItem?.id ?? selectedItem?.orderId;
    if (id == null) return selectedItem;
    const list = this.orders();
    const current = list.find((o: any) => o != null && (o.id === id || String(o.id) === String(id)));
    return current ?? selectedItem;
  }

  /**
   * Whether the order can be moved to "ready to ship" (call ship_order / arrange shipment).
   * Allows orders with business status PROCESSING, CONFIRMED, NEW, READY_TO_SHIP, or shippable delivery statuses.
   */
  isPacked(row: OrderPageVo & { sendStatus?: string; channel?: string }): boolean {
    if (!row) return false;
    const orderStatus = String(row.orderStatus ?? '').toUpperCase();
    if (['PROCESSING', 'CONFIRMED', 'NEW', 'READY_TO_SHIP'].includes(orderStatus)) {
      return true;
    }
    const status = this.getDeliveryStatus(row);
    if (isShopeeChannelCode(row?.channel)) {
      return MarketplaceOrderComponent.SHOPEE_SHIPPABLE_STATUSES.includes(status);
    }
    const shippableStatuses = [
      MarketplaceOrderComponent.SEND_STATUS_PACKED,
      MarketplaceOrderComponent.SEND_STATUS_READY_TO_SHIP_PENDING,
      MarketplaceOrderComponent.SEND_STATUS_READY_TO_SHIP,
      '30510',
      '30511',
    ];
    return shippableStatuses.includes(status);
  }

  /** Whether the given order has delivery status Ready to ship – used to show Print waybill action. */
  isReadyToShip(row: OrderPageVo & { sendStatus?: string }): boolean {
    if (!row) return false;
    const orderStatus = String(row.orderStatus ?? '').toUpperCase();
    if (orderStatus === 'READY_TO_SHIP') {
      return true;
    }
    return this.getDeliveryStatus(row) === MarketplaceOrderComponent.SEND_STATUS_READY_TO_SHIP;
  }

  /** Map outerOrderNo (or outer_order_no) to string list for Shopee APIs. */
  private getOuterOrderSnList(orders: any[]): string[] {
    return orders
      .map((o) => String(o.outerOrderNo ?? o.outer_order_no ?? '').trim())
      .filter((sn) => sn !== '');
  }

  /** Build request body and call ready-to-ship API. */
  private callReadyToShipApi(shopId: number, orders: any[]): void {
    if (!orders.length) return;
    const channel = orders[0]?.channel;

    if (isShopeeChannelCode(channel)) {
      const orderSns = this.getOuterOrderSnList(orders);
      if (!orderSns.length) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant('order.marketplace.ready_to_ship.outer_order_no_required'),
        });
        return;
      }
      const basePath =
        (this.channelIntegrationService as { configuration?: { basePath?: string } })
          .configuration?.basePath ?? '';
      const url = `${basePath.replace(/\/+$/, '')}/v1/channel-integration/shopee/ready-to-ship?shopId=${shopId}`;
      this.loading.set(true);
      // Shopee ready-to-ship is async: BE enqueues to Kafka and returns a ReadyToShipResponseDTO.
      // Per-order SHIPPED/FAILED status is not known at submit time, so 200 = submitted.
      this.http.post<ReadyToShipResponseDTO>(url, orderSns)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err: any) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: err?.error?.message || err?.message || this.translateService.instant('order.marketplace.ready_to_ship.failed'),
            });
            return of(null);
          }),
          finalize(() => this.loading.set(false))
        )
        .subscribe((res) => {
          if (res == null) return;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: res?.message ?? this.translateService.instant('order.marketplace.ready_to_ship.success'),
          });
          this.selectedOrders.set([]);
          this.loadOrders();
        });
    } else {
      const orderIds = this.getOuterOrderIdsAsNumbers(orders);
      if (!orderIds.length) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant('order.marketplace.ready_to_ship.outer_order_no_required'),
        });
        return;
      }
      const body: ReadyToShipRequestDTO = { shopId, orderIds };
      this.loading.set(true);
      this.channelIntegrationService
        .readyToShipOrders(body, 'body', false, { httpHeaderAccept: 'application/json' } as any)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err: any) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: err?.error?.message || err?.message || this.translateService.instant('order.marketplace.ready_to_ship.failed'),
            });
            return of(null);
          }),
          finalize(() => this.loading.set(false))
        )
        .subscribe((res) => {
          if (res == null) return;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('order.marketplace.ready_to_ship.success'),
          });
          this.selectedOrders.set([]);
          this.loadOrders();
        });
    }
  }

  /** Batch Ready to ship: validate all selected have PACKED and same shopId, then call API. */
  onReadyToShipBatch(): void {
    const selected = this.selectedOrders();
    if (!selected.length) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'order.marketplace.ready_to_ship.select_at_least_one'
        ),
      });
      return;
    }
    const nonPacked = selected.filter((o) => !this.isPacked(o));
    if (nonPacked.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'order.marketplace.ready_to_ship.only_packed'
        ),
      });
      return;
    }
    const shopId = selected[0]?.storeId;
    if (shopId == null || shopId === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.ready_to_ship.shop_id_required'),
      });
      return;
    }
    this.callReadyToShipApi(shopId, selected);
  }

  /** Single order Ready to ship (from row action). Only shown when status is PACKED. */
  onReadyToShipRow(rowData: OrderPageVo): void {
    if (!this.isPacked(rowData)) return;
    const shopId = rowData.storeId;
    if (shopId == null || shopId === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.ready_to_ship.shop_id_required'),
      });
      return;
    }
    this.callReadyToShipApi(shopId, [rowData]);
  }

  /** Map outerOrderNo (or outer_order_no) to numbers for API (orderIds = array of outerOrderNo). */
  private getOuterOrderIdsAsNumbers(orders: any[]): number[] {
    return parseOuterOrderNosToNumericIds(orders);
  }

  /** POST to print waybill (Lazada merge-awb or Shopee awb). Response type is application/pdf. */
  private callMergeAwbApi(shopId: number, orders: any[]): void {
    if (!orders.length) return;
    const channel = orders[0]?.channel;
    const basePath =
      (this.channelIntegrationService as { configuration?: { basePath?: string } })
        .configuration?.basePath ?? '';

    let url = '';
    let body: any = null;

    if (isShopeeChannelCode(channel)) {
      const orderSns = this.getOuterOrderSnList(orders);
      if (!orderSns.length) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant('order.marketplace.print_waybill.outer_order_no_required'),
        });
        return;
      }
      url = `${basePath.replace(/\/+$/, '')}/v1/channel-integration/shopee/awb?shopId=${shopId}`;
      body = orderSns;
    } else {
      const orderIds = this.getOuterOrderIdsAsNumbers(orders);
      if (!orderIds.length) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.messages.warning'),
          detail: this.translateService.instant('order.marketplace.print_waybill.outer_order_no_required'),
        });
        return;
      }
      url = buildLazadaMergeAwbUrl(basePath);
      body = { shopId, orderIds };
    }

    this.loading.set(true);
    this.http
      .post(url, body, {
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob',
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: unknown) => {
          const errAny = err as { error?: Blob | { message?: string }; message?: string };
          let detail = errAny?.message;
          if (errAny?.error instanceof Blob) {
            const blob = errAny.error as Blob;
            if (blob.type?.includes('application/json')) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const json = JSON.parse(reader.result as string) as { message?: string };
                  this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('common.messages.error'),
                    detail: json?.message ?? this.translateService.instant('order.marketplace.print_waybill.failed'),
                  });
                } catch {
                  this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('common.messages.error'),
                    detail: this.translateService.instant('order.marketplace.print_waybill.failed'),
                  });
                }
              };
              reader.readAsText(blob);
              return of(null);
            }
          }
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: typeof detail === 'string' ? detail : this.translateService.instant('order.marketplace.print_waybill.failed'),
          });
          return of(null);
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe((blob: Blob | null) => {
        if (blob == null) return;
        const isPdf = blob.type === 'application/pdf' || blob.type === 'application/octet-stream';
        if (isPdf && blob.size > 0) {
          const objectUrl = URL.createObjectURL(blob);
          window.open(objectUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('order.marketplace.print_waybill.success'),
          });
          this.selectedOrders.set([]);
          this.loadOrders();
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('common.messages.warning'),
            detail: this.translateService.instant('order.marketplace.print_waybill.failed'),
          });
        }
      });
  }

  /** Batch Print waybill: only orders with delivery status Ready to ship (10512), same shopId; then call merge-awb. */
  onPrintWaybillBatch(): void {
    const selected = this.selectedOrders();
    if (!selected.length) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.print_waybill.select_at_least_one'),
      });
      return;
    }
    // Use current row from orders() so we read the same delivery status as displayed in the table
    const nonReadyToShip = selected.filter((item) => !this.isReadyToShip(this.resolveToCurrentOrder(item)));
    if (nonReadyToShip.length > 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.print_waybill.only_ready_to_ship'),
      });
      return;
    }
    const shopId = selected[0]?.storeId ?? (selected[0] as any)?.store_id;
    if (shopId == null || shopId === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.print_waybill.shop_id_required'),
      });
      return;
    }
    this.callMergeAwbApi(shopId, selected);
  }

  /** Single order Print waybill (row action). Only shown when delivery status is Ready to ship (10512). */
  onPrintWaybillRow(rowData: OrderPageVo): void {
    if (!this.isReadyToShip(rowData)) return;
    const shopId = rowData.storeId;
    if (shopId == null || shopId === undefined) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant('order.marketplace.print_waybill.shop_id_required'),
      });
      return;
    }
    this.callMergeAwbApi(shopId, [rowData]);
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  onView(rowData: any): void {
    if (!this.canRead()) {
      // Show warning message if needed
      return;
    }
    // Navigate to order detail view page
    const orderId = rowData.id;
    if (orderId) {
      this.router.navigate(['/third/order/view', orderId]);
    }
  }

  onEdit(rowData: any): void {
    if (!this.canUpdate()) {
      // Show warning message if needed
      return;
    }
    // Navigate to edit page - adjust route as needed
    const orderId = rowData.id;
    if (orderId) {
      this.router.navigate(['/order/edit', orderId]);
    }
  }

  onDelete(rowData: any): void {
    if (!this.canDelete()) {
      // Show warning message if needed
      return;
    }
    // Implement delete logic - adjust as needed
    const orderId = rowData.id;
    if (orderId) {
      // Add confirmation dialog and delete API call here
      console.log('Delete order:', orderId);
    }
  }

  onOrderItemDialogVisibleChange(visible: boolean): void {
    this.showOrderItemDialog.set(visible);
    if (!visible) {
      this.selectedOrderId.set(null);
      this.selectedOrderNumber.set(null);
    }
  }

  // Getter methods for template
  get dataSource$() {
    return this.ordersData;
  }

  get totalRecords$() {
    return this.totalRecords;
  }

  get loading$() {
    return this.loading;
  }

  get error$() {
    return this.error;
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
